import { Button, Grid, InputLabel, MenuItem, Select, Switch, Typography } from '@mui/material';
import {
  Asset,
  Configuration,
  CONFIGURATION_TYPE,
  IssueType,
  MIXPANEL_EVENTS,
  PermissionsEnum,
  ProcureUser,
  ProcureVendor,
  WorkOrder,
  WorkOrderPriority,
  WorkOrderStatus,
  WorkOrderUpdateInput,
} from 'vendor-marketplace-shared-library';
import InLineEditTextField from '../../../components/common/InLineEditTextField';
import { WatchersSelection } from '../../../components/work-orders/WatchersSelection';
import React, { useState } from 'react';
import Modal from '../../../components/common/Modal';
import useAuth from '../../../hooks/useAuth';
import useConfigurations from '../../../hooks/useConfigurations';
import { getConfigurationValue } from '../../../utilities/helpers';
import useMixpanel from '../../../hooks/useMixpanel';
import { DropdownWithCheckboxes } from '../../../components/common/DropdownWithCheckboxes';
import PrimaryButton from '../../../components/common/styled-mui-overrides/PrimaryButton';

type WorkOrderDetailsModuleProps = {
  workOrder: WorkOrder;
  users: ProcureUser[];
  workOrderStatuses: WorkOrderStatus[];
  workOrderPriorityData: WorkOrderPriority[];
  issueTypeData: IssueType[];
  procureVendorsData: ProcureVendor[];
  updateWorkOrder: (data: Partial<WorkOrderUpdateInput>, mixpanelEvent: string | null) => void;
  workOrderWatcherUserIds: string[];
  setWorkOrderWatcherUserIds: (watchers: string[]) => void;
  workOrderAssetIds: string[];
  setWorkOrderAssetIds: (assets: string[]) => void;
  assets: Asset[];
};

export default function WorkOrderDetailsModule(props: WorkOrderDetailsModuleProps) {
  const { validateWorkOrderUserPermissions } = useAuth();
  const { mixpanelTrack } = useMixpanel();
  const { configurationsQuery } = useConfigurations();
  const { workOrder } = props;
  const [openConciergeEnabledConfirmationModal, setOpenConciergeEnabledConfirmationModal] =
    useState(false);
  const [anchorElForAsset, setAnchorElForAsset] = useState<HTMLElement | null>(null);

  const userConciergeFeatureFrontendEnabledConfiguration =
    configurationsQuery?.data?.getConfigurations?.find(
      (configuration: Configuration) =>
        configuration.name === CONFIGURATION_TYPE.CONCIERGE_FEATURE_FRONTEND_IS_ENABLED,
    );

  const isUserConciergeFeatureFrontendEnabled = getConfigurationValue(
    userConciergeFeatureFrontendEnabledConfiguration,
  );

  const userWarrantyProductIsEnabledConfig = configurationsQuery?.data?.getConfigurations?.find(
    (configuration: Configuration) =>
      configuration.name === CONFIGURATION_TYPE.WARRANTY_PRODUCT_IS_ENABLED,
  );

  const isUserWarrantyProductIsEnabled = getConfigurationValue(userWarrantyProductIsEnabledConfig);

  const userConciergeVendorsIsEnabledConfiguration =
    configurationsQuery?.data?.getConfigurations?.find(
      (configuration: Configuration) =>
        configuration.name === CONFIGURATION_TYPE.CONCIERGE_VENDORS_IS_ENABLED,
    );

  const isUserConciergeVendorsEnabled = getConfigurationValue(
    userConciergeVendorsIsEnabledConfiguration,
  );

  const userNonWarrantyWorkOrderCreationIsEnabledConfiguration =
    configurationsQuery?.data?.getConfigurations?.find(
      (configuration: Configuration) =>
        configuration.name === CONFIGURATION_TYPE.NON_WARRANTY_WORK_ORDER_CREATION_IS_ENABLED,
    );

  const isUserNonWarrantyWorkOrderCreationIsEnabled = getConfigurationValue(
    userNonWarrantyWorkOrderCreationIsEnabledConfiguration,
  );

  return (
    <Grid container direction={'column'} rowGap={2}>
      <Typography variant={'h6'}>Details</Typography>
      <Grid
        container
        item
        sx={{ backgroundColor: 'secondary.light' }}
        p={2}
        rowGap={1}
        borderRadius={2}
        boxShadow={2}
      >
        {/*Description*/}
        <Grid item container direction={'column'}>
          <Grid item>
            <Typography variant={'caption'}>Description</Typography>
          </Grid>
          <Grid item>
            {validateWorkOrderUserPermissions(
              workOrder,
              PermissionsEnum.WORK_ORDER_DETAILS_UPDATE,
            ) ? (
              <InLineEditTextField
                content={workOrder.description || ''}
                onSave={value => {
                  props.updateWorkOrder(
                    {
                      description: value,
                    },
                    MIXPANEL_EVENTS.WORK_ORDER_DETAILS_EDIT_WORK_ORDER_PRESSED,
                  );
                }}
              />
            ) : (
              <Typography>{workOrder.description || ''}</Typography>
            )}
          </Grid>
        </Grid>

        {/*Assignee*/}
        <Grid item container direction={'column'}>
          <Grid item>
            <Typography variant={'caption'}>Assignee</Typography>
          </Grid>
          <Grid item>
            {validateWorkOrderUserPermissions(
              workOrder,
              PermissionsEnum.WORK_ORDER_DETAILS_UPDATE,
            ) ? (
              <Select
                size={'small'}
                value={workOrder.assignedUser?.id}
                defaultValue={workOrder.assignedUser?.id}
                onChange={e => {
                  mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_UPDATE_ASSIGNEE_ATTEMPTED);
                  props.updateWorkOrder(
                    { assignedUserId: Number(e.target.value) },
                    MIXPANEL_EVENTS.WORK_ORDER_UPDATE_ASSIGNEE_COMPLETED,
                  );
                }}
              >
                {props.users.map((assignee: ProcureUser) => {
                  return (
                    <MenuItem key={assignee.id} value={assignee.id}>
                      {assignee.fullName}
                    </MenuItem>
                  );
                })}
              </Select>
            ) : (
              <Typography>{workOrder.assignedUser?.fullName}</Typography>
            )}
          </Grid>
        </Grid>

        {/*Work Order Status*/}
        <Grid item container direction={'column'}>
          <Grid item>
            <Typography variant={'caption'}>Work Order Status</Typography>
          </Grid>
          <Grid item>
            {validateWorkOrderUserPermissions(
              workOrder,
              PermissionsEnum.WORK_ORDER_DETAILS_UPDATE,
            ) && !isUserWarrantyProductIsEnabled ? (
              <Select
                size={'small'}
                value={workOrder.workOrderStatus?.id}
                defaultValue={workOrder.workOrderStatus?.id}
                onChange={e => {
                  mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_UPDATE_STATUS_ATTEMPTED);
                  props.updateWorkOrder(
                    { workOrderStatusId: Number(e.target.value) },
                    MIXPANEL_EVENTS.WORK_ORDER_UPDATE_STATUS_COMPLETED,
                  );
                }}
              >
                {props.workOrderStatuses.map((status: WorkOrderStatus) => {
                  return (
                    <MenuItem key={status.id} value={status.id}>
                      {status.name}
                    </MenuItem>
                  );
                })}
              </Select>
            ) : (
              <Typography>{workOrder.workOrderStatus?.name}</Typography>
            )}
          </Grid>
        </Grid>

        {/*Procure Vendor*/}
        {isUserConciergeVendorsEnabled && (
          <Grid item container direction={'column'}>
            <Grid item>
              <Typography variant={'caption'}>Assigned Vendor</Typography>
            </Grid>
            <Grid item>
              {validateWorkOrderUserPermissions(
                workOrder,
                PermissionsEnum.WORK_ORDER_DETAILS_UPDATE,
              ) ? (
                <Select
                  size={'small'}
                  value={workOrder.procureVendor?.id}
                  defaultValue={workOrder.procureVendor?.id || ''}
                  onChange={e => props.updateWorkOrder({ procureVendorId: e.target.value }, null)}
                >
                  {props.procureVendorsData.map((vendor: ProcureVendor) => {
                    return (
                      <MenuItem key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              ) : (
                <Typography>{workOrder.procureVendor?.name || 'None'}</Typography>
              )}
            </Grid>
          </Grid>
        )}

        {/*Priority*/}
        {isUserNonWarrantyWorkOrderCreationIsEnabled && (
          <Grid item container direction={'column'}>
            <Grid item>
              <Typography variant={'caption'}>Priority</Typography>
            </Grid>
            <Grid item>
              {validateWorkOrderUserPermissions(
                workOrder,
                PermissionsEnum.WORK_ORDER_DETAILS_UPDATE,
              ) ? (
                <Select
                  size={'small'}
                  value={workOrder.workOrderPriority?.id}
                  defaultValue={workOrder.workOrderPriority?.id}
                  onChange={e => {
                    mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_UPDATE_PRIORITY_ATTEMPTED);
                    props.updateWorkOrder(
                      { workOrderPriorityId: Number(e.target.value) },
                      MIXPANEL_EVENTS.WORK_ORDER_UPDATE_PRIORITY_COMPLETED,
                    );
                  }}
                >
                  {props.workOrderPriorityData.map((priority: WorkOrderPriority) => {
                    return (
                      <MenuItem key={priority.id} value={priority.id}>
                        {priority.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              ) : (
                <Typography>{workOrder.workOrderPriority?.name}</Typography>
              )}
            </Grid>
          </Grid>
        )}

        {/*Issue Type*/}
        <Grid item container direction={'column'}>
          <Grid item>
            <Typography variant={'caption'}>Issue Type</Typography>
          </Grid>
          <Grid item>
            {props.issueTypeData.length > 1 ? (
              <Select
                size={'small'}
                value={workOrder.issueType?.id}
                defaultValue={workOrder.issueType?.id}
                onChange={e => {
                  mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_UPDATE_ISSUE_TYPE_ATTEMPTED);
                  props.updateWorkOrder(
                    { issueTypeId: Number(e.target.value) },
                    MIXPANEL_EVENTS.WORK_ORDER_UPDATE_ISSUE_TYPE_COMPLETED,
                  );
                }}
              >
                {props.issueTypeData.map((issueType: IssueType) => {
                  return (
                    <MenuItem key={issueType.id} value={issueType.id}>
                      {issueType.name}
                    </MenuItem>
                  );
                })}
              </Select>
            ) : (
              <Typography>{props.issueTypeData[0]?.name ?? 'None'}</Typography>
            )}
          </Grid>
        </Grid>

        {/*Property*/}
        <Grid item container flexDirection={'column'}>
          <Grid item>
            <Typography variant={'caption'}>Property</Typography>
          </Grid>
          <Grid item>
            <Typography>{workOrder.property!.name}</Typography>
          </Grid>
        </Grid>

        {/*Watchers*/}
        <Grid item container direction={'column'} sx={{ alignItems: 'flexStart' }}>
          <Grid item>
            <Typography variant={'caption'}>Watchers</Typography>
          </Grid>
          <Grid item>
            <WatchersSelection
              watcherIds={props.workOrderWatcherUserIds}
              setWatcherIds={props.setWorkOrderWatcherUserIds}
              users={props.users}
              onSave={() => {
                mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_UPDATE_FOLLOWERS_ATTEMPTED);
                props.updateWorkOrder(
                  {
                    workOrderWatcherUserIds: props.workOrderWatcherUserIds.map(id => Number(id)),
                  },
                  MIXPANEL_EVENTS.WORK_ORDER_UPDATE_FOLLOWERS_COMPLETED,
                );
              }}
            />
          </Grid>
        </Grid>

        {/*Assets*/}
        {props.workOrderAssetIds && props.workOrderAssetIds.length > 0 && (
          <Grid item xs={12}>
            <Grid item>
              <Typography variant={'caption'}>Assets assigned to this WO:</Typography>
            </Grid>
            {props.assets
              .filter(asset => props.workOrderAssetIds?.includes(asset.id))
              .map(asset => (
                <Grid item xs={12} key={asset.id}>
                  <Typography>{`${asset.make}-${asset.model}`}</Typography>
                </Grid>
              ))}
          </Grid>
        )}

        {props.assets.length > 0 && (
          <Grid item xs={12}>
            <PrimaryButton
              onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorElForAsset(e.currentTarget)}
            >
              Update assets
            </PrimaryButton>
            <DropdownWithCheckboxes
              entities={props.assets}
              onClose={() => {
                setAnchorElForAsset(null);
                props.updateWorkOrder(
                  {
                    workOrderAssetIds: props.workOrderAssetIds.map(id => Number(id)),
                  },
                  null,
                );
              }}
              onSave={() => {}}
              anchorEl={anchorElForAsset}
              selectedIds={props.workOrderAssetIds}
              setSelectedIds={props.setWorkOrderAssetIds}
              displayFieldKey={'make'}
            />
          </Grid>
        )}

        {/*Concierge Enabled*/}
        {isUserConciergeFeatureFrontendEnabled && (
          <Grid item container direction={'column'}>
            <Grid item>
              <Typography variant={'caption'}>
                {workOrder.conciergeEnabled
                  ? 'Bids Requested'
                  : validateWorkOrderUserPermissions(
                      workOrder,
                      PermissionsEnum.WORK_ORDER_DETAILS_UPDATE,
                    ) && isUserConciergeFeatureFrontendEnabled
                  ? 'Request bids from ProCure for this work order'
                  : ''}
              </Typography>
            </Grid>
            {validateWorkOrderUserPermissions(
              workOrder,
              PermissionsEnum.WORK_ORDER_DETAILS_UPDATE,
            ) &&
              isUserConciergeFeatureFrontendEnabled && (
                <Grid item>
                  <Switch
                    checked={workOrder.conciergeEnabled!}
                    onChange={() => {
                      if (workOrder.conciergeEnabled) {
                        setOpenConciergeEnabledConfirmationModal(true);
                      } else {
                        mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_UPDATE_TOGGLE_CONCIERGE_ATTEMPTED);
                        props.updateWorkOrder(
                          { conciergeEnabled: !workOrder.conciergeEnabled },
                          MIXPANEL_EVENTS.WORK_ORDER_UPDATE_TOGGLE_CONCIERGE_COMPLETED,
                        );
                      }
                    }}
                  />
                </Grid>
              )}
          </Grid>
        )}

        {/*Created by*/}
        <Grid item container direction={'column'}>
          <Grid item>
            <Typography variant={'caption'}>Created by</Typography>
          </Grid>
          <Grid item>
            <Typography>{workOrder.createdByUser!.fullName}</Typography>
          </Grid>
        </Grid>
      </Grid>
      <Modal
        title={'Cancel bid request?'}
        open={openConciergeEnabledConfirmationModal}
        setOpen={setOpenConciergeEnabledConfirmationModal}
      >
        <Grid item container direction={'column'} rowGap={2}>
          <Grid item>
            <Typography>
              Are you sure you want to cancel your request for bids on this work order?
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant={'contained'}
              color={'error'}
              onClick={() => {
                mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_UPDATE_TOGGLE_CONCIERGE_ATTEMPTED);
                props.updateWorkOrder(
                  { conciergeEnabled: !workOrder.conciergeEnabled },
                  MIXPANEL_EVENTS.WORK_ORDER_UPDATE_TOGGLE_CONCIERGE_COMPLETED,
                );
                setOpenConciergeEnabledConfirmationModal(false);
              }}
            >
              Cancel request
            </Button>
          </Grid>
        </Grid>
      </Modal>
    </Grid>
  );
}
